import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api/axios';
import { FiMail, FiLock, FiUser, FiPhone, FiEye, FiEyeOff } from 'react-icons/fi';
import './AuthPages.css';

const RegisterPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });

    useEffect(() => {
        if (!token) {
            setError('No invitation token provided. You need an invitation to register.');
            setLoading(false);
            return;
        }

        // Validate invitation token
        api.get(`/auth/invitation/${token}`)
            .then(response => {
                setInvitation(response.data);
                if (response.data.email) {
                    setFormData(prev => ({ ...prev, email: response.data.email }));
                }
            })
            .catch(err => {
                setError(err.response?.data?.message || 'Invalid or expired invitation.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setSubmitting(true);

        try {
            await api.post('/auth/register', {
                token,
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
            });

            navigate('/login', {
                state: { message: 'Registration successful! Please login.' }
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="auth-page">
                <div className="loading-container">
                    <div className="spinner spinner-lg"></div>
                    <p className="mt-4">Validating invitation...</p>
                </div>
            </div>
        );
    }

    if (!invitation) {
        return (
            <div className="auth-page">
                <div className="auth-container">
                    <div className="auth-form-container" style={{ maxWidth: '500px', margin: 'auto' }}>
                        <div className="auth-form-wrapper">
                            <div className="auth-header text-center">
                                <span style={{ fontSize: '4rem' }}>🔒</span>
                                <h2>Invalid Invitation</h2>
                                <p>{error}</p>
                            </div>
                            <Link to="/login" className="btn btn-primary w-full mt-4">
                                Go to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                {/* Left Side - Branding */}
                <div className="auth-branding">
                    <div className="branding-content">
                        <div className="branding-logo">
                            <img src="/cravings_logo.png" alt="Cravings Logo" className="logo-img-large" />
                        </div>
                        <p className="branding-tagline">
                            Order & Inventory Management System
                        </p>
                        <div className="invitation-info">
                            <h3>You've been invited!</h3>
                            <p>You are being registered as:</p>
                            <div className="invitation-details">
                                <div className="detail-item">
                                    <span className="detail-label">Branch:</span>
                                    <span className="detail-value">{invitation.branchName}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Role:</span>
                                    <span className="detail-value">
                                        {invitation.role === 'branch_admin' ? 'Branch Admin' : 'Staff'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Registration Form */}
                <div className="auth-form-container">
                    <div className="auth-form-wrapper">
                        <div className="auth-header">
                            <h2>Create Account</h2>
                            <p>Fill in your details to complete registration</p>
                        </div>

                        {error && (
                            <div className="alert alert-error">
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="auth-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">First Name *</label>
                                    <div className="input-with-icon">
                                        <FiUser className="input-icon" />
                                        <input
                                            type="text"
                                            name="firstName"
                                            className="form-input"
                                            placeholder="First name"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Last Name</label>
                                    <div className="input-with-icon">
                                        <FiUser className="input-icon" />
                                        <input
                                            type="text"
                                            name="lastName"
                                            className="form-input"
                                            placeholder="Last name"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Email Address *</label>
                                <div className="input-with-icon">
                                    <FiMail className="input-icon" />
                                    <input
                                        type="email"
                                        name="email"
                                        className="form-input"
                                        placeholder="Enter your email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        readOnly={!!invitation}
                                        style={invitation ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Phone Number</label>
                                <div className="input-with-icon">
                                    <FiPhone className="input-icon" />
                                    <input
                                        type="tel"
                                        name="phone"
                                        className="form-input"
                                        placeholder="Enter phone number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Password *</label>
                                <div className="input-with-icon">
                                    <FiLock className="input-icon" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        className="form-input"
                                        placeholder="Create password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        className="input-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <FiEyeOff /> : <FiEye />}
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Confirm Password *</label>
                                <div className="input-with-icon">
                                    <FiLock className="input-icon" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="confirmPassword"
                                        className="form-input"
                                        placeholder="Confirm password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-lg w-full"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <div className="spinner"></div>
                                        <span>Creating Account...</span>
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </button>
                        </form>

                        <div className="auth-footer">
                            <p className="text-center text-muted text-sm">
                                Already have an account? <Link to="/login">Sign in</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
